import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageSquare,
  X,
  Send,
  Sparkles,
  Calendar,
  Users,
  Clock,
  BarChart2,
  Settings,
  Zap,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Trash2,
  Edit3,
  Search,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/auth/hooks';
import toast from 'react-hot-toast';
import { RealTimeActions, ActionItem } from './RealTimeActions';

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  action?: {
    type: 'create' | 'update' | 'delete' | 'query';
    entity?: string;
    success?: boolean;
  };
  requiresConfirmation?: boolean;
  confirmationData?: any;
}

interface TokensInfo {
  available: boolean;
  tokens_limit: number | null;
  tokens_used: number;
  tokens_remaining: number | null;
  display_limit: number | null;
  display_used: number | null;
  display_remaining: number | null;
}

interface Capability {
  icon: any;
  title: string;
  description: string;
  examples: string[];
  color: string;
}

const CAPABILITIES: Capability[] = [
  {
    icon: Calendar,
    title: 'ניהול תורים מתקדם',
    description: '60+ כלים לניהול מושלם',
    examples: ['מה התורים היום?', 'קבע תור למחר ב-14:00', 'תורים ממתינים לאישור'],
    color: 'from-blue-500 to-cyan-500',
  },
  {
    icon: Users,
    title: 'ניהול לקוחות חכם',
    description: 'חיפוש, הוספה וניתוח נאמנות',
    examples: ['הוסף לקוח: שרה 050-1234567', 'מי הלקוחות הנאמנים?', 'חפש לקוח דנה'],
    color: 'from-purple-500 to-pink-500',
  },
  {
    icon: BarChart2,
    title: 'דוחות ותובנות',
    description: 'אנליטיקה מלאה לעסק',
    examples: ['כמה הרווחתי השבוע?', 'שירותים פופולריים', 'השוואת תקופות'],
    color: 'from-green-500 to-emerald-500',
  },
  {
    icon: Settings,
    title: 'ניהול עסק',
    description: 'מידע ושליטה מלאה',
    examples: ['מידע על העסק', 'מי בצוות שלי?', 'שעות פעילות'],
    color: 'from-orange-500 to-red-500',
  },
];

const QUICK_ACTIONS = [
  { icon: Calendar, text: 'מה התורים היום?', color: 'bg-blue-50 text-blue-600 hover:bg-blue-100' },
  { icon: Users, text: '5 לקוחות אחרונים', color: 'bg-purple-50 text-purple-600 hover:bg-purple-100' },
  { icon: BarChart2, text: 'כמה הרווחתי השבוע?', color: 'bg-green-50 text-green-600 hover:bg-green-100' },
  { icon: Zap, text: 'תורים ממתינים לאישור', color: 'bg-orange-50 text-orange-600 hover:bg-orange-100' },
  { icon: Clock, text: 'לקוחות נאמנים', color: 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100' },
  { icon: Search, text: 'שירותים פופולריים', color: 'bg-cyan-50 text-cyan-600 hover:bg-cyan-100' },
];

interface AIAssistantProps {
  tokensInfo: TokensInfo | null;
  isBlocked?: boolean;
  onClose?: () => void;
}

export function AIAssistant({ tokensInfo, isBlocked = false, onClose }: AIAssistantProps) {
  const { user } = useAuth();
  const businessId = user?.user_metadata?.business_id;

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showCapabilities, setShowCapabilities] = useState(true);
  const [pendingConfirmation, setPendingConfirmation] = useState<any>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [thinkingStatus, setThinkingStatus] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<any>(null);
  const [realtimeActions, setRealtimeActions] = useState<ActionItem[]>([]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([
        {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: `שלום! 👋 אני העוזר החכם שלך עם 60+ כלים מתקדמים!\n\n✨ יכולות מיוחדות:\n📅 ניהול תורים - "מה התורים היום?", "קבע תור למחר ב-14:00"\n👥 ניהול לקוחות - "הוסף לקוח: שרה 050-1234567", "חפש לקוח דנה"\n📊 דוחות מתקדמים - "כמה הרווחתי השבוע?", "שירותים פופולריים"\n⚙️ ניהול עסק - "מידע על העסק", "מי בצוות שלי?"\n🔍 אינטליגנציה - אני מבין תאריכים טבעיים כמו "מחר", "בשבוע הבא"\n\nמה תרצה לעשות?`,
          timestamp: new Date(),
        },
      ]);
    }
  }, [isOpen]);

  const handleSendMessage = async (messageText?: string) => {
    const text = messageText || inputValue.trim();
    if (!text || isLoading || isBlocked) return;

    const userMessage: Message = {
      id: crypto.randomUUID(),
      role: 'user',
      content: text,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setShowCapabilities(false);
    setThinkingStatus('מעבד את הבקשה...');
    setRealtimeActions([]);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;

      // Use AI Agent V3 with advanced features
      const response = await fetch(
        `https://nkuqcyelxgyihrxyvitb.supabase.co/functions/v1/ai-agent-v3`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token || ''}`,
          },
          body: JSON.stringify({
            message: text,
            businessId,
            userId: user?.id,
            sessionId,
          }),
        }
      );

      const data = await response.json();

      if (data.session_id && !sessionId) {
        setSessionId(data.session_id);
      }

      if (data.thinking_status) {
        setThinkingStatus(data.thinking_status);
      }

      // Create visual actions based on response
      const actions: ActionItem[] = [];

      // Add thinking status as first action
      if (data.thinking_status) {
        actions.push({
          id: `action-thinking-${Date.now()}`,
          name: 'processing',
          status: 'completed' as const,
          message: data.thinking_status,
          timestamp: new Date(),
        });
      }

      // Add from thinking_history if available
      if (data.thinking_history && Array.isArray(data.thinking_history)) {
        data.thinking_history.forEach((actionMsg: string, idx: number) => {
          actions.push({
            id: `action-${Date.now()}-${idx}`,
            name: data.toolsUsed?.[idx] || 'unknown',
            status: 'completed' as const,
            message: actionMsg,
            timestamp: new Date(),
          });
        });
      }

      // Add from toolsUsed if available
      if (data.toolsUsed && Array.isArray(data.toolsUsed)) {
        data.toolsUsed.forEach((tool: string, idx: number) => {
          actions.push({
            id: `action-tool-${Date.now()}-${idx}`,
            name: tool,
            status: 'completed' as const,
            message: `${tool} הושלם בהצלחה`,
            timestamp: new Date(),
          });
        });
      }

      // Fallback: if no actions but we got a reply, show generic processing
      if (actions.length === 0 && data.reply) {
        actions.push({
          id: `action-generic-${Date.now()}`,
          name: 'process_request',
          status: 'completed' as const,
          message: 'מעבד את הבקשה',
          timestamp: new Date(),
        });
        actions.push({
          id: `action-generate-${Date.now()}`,
          name: 'generate_response',
          status: 'completed' as const,
          message: 'מייצר תשובה',
          timestamp: new Date(),
        });
      }

      setRealtimeActions(actions);

      if (data.pending_action) {
        setPendingAction(data.pending_action);
      } else {
        setPendingAction(null);
      }

      if (data.error) {
        throw new Error(data.error);
      }

      // Handle confirmation requirement
      if (data.requireConfirmation) {
        setPendingConfirmation(data);
        const confirmMessage: Message = {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: data.warning || 'האם לאשר פעולה זו?',
          timestamp: new Date(),
          requiresConfirmation: true,
          confirmationData: data,
        };
        setMessages((prev) => [...prev, confirmMessage]);
        setIsLoading(false);
        return;
      }

      // Handle Agent V3 response format
      let content = '';

      if (data.reply) {
        // Agent V3 returns 'reply' field with formatted response
        content = data.reply;
      } else if (data.message) {
        content = data.message;
      } else if (data.result) {
        // Fallback for other formats
        if (typeof data.result === 'string') {
          content = data.result;
        } else if (Array.isArray(data.result)) {
          content = `מצאתי ${data.result.length} תוצאות:\n${JSON.stringify(data.result, null, 2)}`;
        } else {
          content = JSON.stringify(data.result, null, 2);
        }
      } else {
        // If nothing specific, show the entire response
        content = JSON.stringify(data, null, 2);
      }

      const assistantMessage: Message = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content,
        timestamp: new Date(),
        action: data.toolsUsed?.length > 0 ? { type: 'query', success: true, entity: data.toolsUsed.join(', ') } : undefined,
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error: any) {

      const errorMessage: Message = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: `אופס! משהו השתבש: ${error.message || 'נסה שוב'}`,
        timestamp: new Date(),
        action: { type: 'query', success: false },
      };
      setMessages((prev) => [...prev, errorMessage]);
      toast.error('שגיאה בשליחת הבקשה');
    } finally {
      setIsLoading(false);
      setThinkingStatus(null);
    }
  };

  const handleConfirm = async () => {
    if (!pendingConfirmation) return;

    setIsLoading(true);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(
        `${supabaseUrl}/functions/v1/ai-agent-v3`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token || ''}`,
          },
          body: JSON.stringify({
            message: pendingConfirmation.sql ? '' : messages[messages.length - 2]?.content || '',
            businessId,
            userId: user?.id,
            confirm: true,
            sql: pendingConfirmation.sql,
          }),
        }
      );

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error);
      }

      const assistantMessage: Message = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: data.reply || 'הפעולה בוצעה בהצלחה',
        timestamp: new Date(),
        action: { type: 'delete', success: true },
      };

      setMessages((prev) => [...prev, assistantMessage]);
      setPendingConfirmation(null);
    } catch (error: any) {

      toast.error('שגיאה בביצוע הפעולה');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setPendingConfirmation(null);
    const cancelMessage: Message = {
      id: crypto.randomUUID(),
      role: 'assistant',
      content: 'הפעולה בוטלה. במה עוד אוכל לעזור?',
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, cancelMessage]);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Floating Button */}
      {!isOpen && (
        <motion.button
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => setIsOpen(true)}
          className="fixed bottom-24 left-6 z-40 p-4 bg-gradient-to-br from-blue-600 to-purple-600 text-white rounded-full shadow-2xl hover:shadow-blue-500/50 transition-all"
        >
          <div className="relative">
            <MessageSquare className="h-6 w-6" />
            <motion.div
              className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full"
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ repeat: Infinity, duration: 2 }}
            />
          </div>
        </motion.button>
      )}

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 100, scale: 0.8 }}
            className="fixed bottom-24 left-6 z-50 w-[420px] h-[600px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="relative bg-gradient-to-br from-blue-600 to-purple-600 text-white p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="p-2 bg-white/20 backdrop-blur-sm rounded-xl">
                      <Sparkles className="h-6 w-6" />
                    </div>
                    <motion.div
                      className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-white"
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ repeat: Infinity, duration: 2 }}
                    />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">עוזר AI מתקדם</h3>
                    <p className="text-xs text-white/80">60+ כלים חכמים</p>
                  </div>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Tokens Info */}
              {tokensInfo && (
                <div className="mt-3 p-2 bg-white/10 backdrop-blur-sm rounded-lg">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-white/80">טוקנים שנותרו</span>
                    <span className="font-semibold">
                      {tokensInfo.display_remaining?.toLocaleString() || 0} / {tokensInfo.display_limit?.toLocaleString() || 0}
                    </span>
                  </div>
                  <div className="w-full h-1.5 bg-white/20 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{
                        width: `${Math.max(
                          0,
                          ((tokensInfo.display_remaining || 0) / (tokensInfo.display_limit || 1)) * 100
                        )}%`,
                      }}
                      className="h-full bg-gradient-to-r from-green-400 to-emerald-400 rounded-full"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
              {/* Capabilities Cards */}
              {showCapabilities && messages.length <= 1 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-3"
                >
                  <p className="text-sm font-semibold text-gray-700 text-center mb-4">בחר יכולת ונתחיל:</p>
                  {CAPABILITIES.map((cap, index) => {
                    const Icon = cap.icon;
                    return (
                      <motion.button
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        onClick={() => handleSendMessage(cap.examples[0])}
                        className="w-full p-3 bg-white rounded-xl shadow-sm hover:shadow-md transition-all text-right group"
                      >
                        <div className="flex items-start gap-3">
                          <div className={`p-2 bg-gradient-to-br ${cap.color} rounded-lg text-white`}>
                            <Icon className="h-5 w-5" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                              {cap.title}
                            </h4>
                            <p className="text-xs text-gray-500 mt-1">{cap.description}</p>
                          </div>
                        </div>
                      </motion.button>
                    );
                  })}
                </motion.div>
              )}

              {/* Chat Messages */}
              {messages.map((message, index) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl p-3 ${
                      message.role === 'user'
                        ? 'bg-gradient-to-br from-blue-600 to-purple-600 text-white rounded-br-sm'
                        : 'bg-white text-gray-900 rounded-bl-sm shadow-sm'
                    }`}
                  >
                    {message.action && (
                      <div
                        className={`flex items-center gap-2 text-xs mb-2 ${
                          message.role === 'user' ? 'text-white/80' : 'text-gray-500'
                        }`}
                      >
                        {message.action.success ? (
                          <CheckCircle2 className="h-3 w-3 text-green-500" />
                        ) : (
                          <AlertCircle className="h-3 w-3 text-red-500" />
                        )}
                        <span>
                          {message.action.type === 'query' && 'שאילתה'}
                          {message.action.type === 'create' && 'יצירה'}
                          {message.action.type === 'update' && 'עדכון'}
                          {message.action.type === 'delete' && 'מחיקה'}
                        </span>
                      </div>
                    )}
                    <p className="whitespace-pre-wrap text-sm leading-relaxed">{message.content}</p>
                    {message.requiresConfirmation && (
                      <div className="flex gap-2 mt-3">
                        <button
                          onClick={handleConfirm}
                          disabled={isLoading}
                          className="flex-1 px-3 py-1.5 bg-red-500 text-white rounded-lg text-xs font-medium hover:bg-red-600 disabled:opacity-50 transition-colors"
                        >
                          אשר
                        </button>
                        <button
                          onClick={handleCancel}
                          disabled={isLoading}
                          className="flex-1 px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-300 disabled:opacity-50 transition-colors"
                        >
                          בטל
                        </button>
                      </div>
                    )}
                    <p
                      className={`text-xs mt-1 ${
                        message.role === 'user' ? 'text-white/60' : 'text-gray-400'
                      }`}
                    >
                      {message.timestamp.toLocaleTimeString('he-IL', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                </motion.div>
              ))}

              {isLoading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex justify-start"
                >
                  <div className="bg-white rounded-2xl rounded-bl-sm p-3 shadow-sm">
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                      <span className="text-sm text-gray-600">מעבד...</span>
                    </div>
                  </div>
                </motion.div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Quick Actions */}
            {!isBlocked && messages.length > 1 && (
              <div className="px-4 py-2 bg-white border-t border-gray-100">
                <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-none">
                  {QUICK_ACTIONS.map((action, index) => {
                    const Icon = action.icon;
                    return (
                      <motion.button
                        key={index}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.05 }}
                        onClick={() => handleSendMessage(action.text)}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${action.color}`}
                      >
                        <Icon className="h-3 w-3" />
                        {action.text}
                      </motion.button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Real-Time Actions Display */}
            <RealTimeActions
              actions={realtimeActions}
              isVisible={isLoading || realtimeActions.length > 0}
            />

            {/* Thinking Status Display */}
            {thinkingStatus && isLoading && realtimeActions.length === 0 && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="px-4 py-2 bg-blue-50 border-t border-blue-100"
              >
                <div className="flex items-center gap-2 text-sm text-blue-600">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>{thinkingStatus}</span>
                </div>
              </motion.div>
            )}

            {/* Pending Action Display */}
            {pendingAction && !isLoading && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="px-4 py-2 bg-amber-50 border-t border-amber-100"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-amber-700">
                    <AlertCircle className="h-4 w-4" />
                    <span>
                      ממתין לתשובה: {pendingAction.last_question || 'מה הפרטים?'}
                    </span>
                  </div>
                  <button
                    onClick={() => {
                      setPendingAction(null);
                      setSessionId(null);
                      toast.success('הפעולה בוטלה');
                    }}
                    className="text-xs text-amber-600 hover:text-amber-800 underline"
                  >
                    בטל
                  </button>
                </div>
                {pendingAction.collected_params && Object.keys(pendingAction.collected_params).length > 0 && (
                  <div className="mt-1 text-xs text-amber-600">
                    נאסף: {Object.entries(pendingAction.collected_params).map(([k, v]) => `${k}: ${v}`).join(', ')}
                  </div>
                )}
              </motion.div>
            )}

            {/* Input Area */}
            <div className="p-4 bg-white border-t border-gray-100">
              {isBlocked ? (
                <div className="text-center py-3 px-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600 font-medium">
                    נגמרו הטוקנים בחבילה הנוכחית
                  </p>
                  <button className="mt-2 text-xs text-red-600 underline hover:text-red-700">
                    שדרג חבילה
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-end gap-2">
                    <textarea
                      ref={inputRef}
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder={
                        pendingAction
                          ? pendingAction.last_question || 'הקלד הודעה...'
                          : isLoading
                          ? 'מעבד...'
                          : 'הקלד הודעה...'
                      }
                      disabled={isLoading || isBlocked}
                      className="flex-1 resize-none bg-gray-100 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 max-h-24"
                      rows={1}
                    />
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleSendMessage()}
                      disabled={!inputValue.trim() || isLoading || isBlocked}
                      className="p-2.5 bg-gradient-to-br from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      <Send className="h-5 w-5" />
                    </motion.button>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
